/**
 * useUserRole — central source of truth for the logged-in user's role.
 *
 * Reads from localStorage.user_details.roles[*].employee_role.
 * Resolves the user's primary role using a fixed priority list so a user
 * with multiple roles always lands on a deterministic dashboard.
 */
import { useMemo } from 'react';

// Canonical role keys used across the role-based dashboards.
// Keep in sync with the backend widget registry / role-mapping.
export const ROLE_KEYS = {
  ADMIN: 'ADMIN',
  OPERATOR: 'OPERATOR',
  SUPERVISOR: 'SUPERVISOR',
  QA_REVIEWER: 'QA_REVIEWER',
  QA_MANAGER: 'QA_MANAGER',
  PLANT_HEAD: 'PLANT_HEAD',
  QC: 'QC',
  CORPORATE: 'CORPORATE',
  // Planner has no dashboard of its own yet: it is reported in allRoles / isPlanner and the
  // primary role falls through to DEFAULT (admin cockpit) unless the user holds another role.
  PLANNER: 'PLANNER',
  DEFAULT: 'DEFAULT',
};

// Priority order — when a user has multiple roles, the highest one in this
// list wins for dashboard rendering.
const ROLE_PRIORITY = [
  ROLE_KEYS.ADMIN,
  ROLE_KEYS.CORPORATE,
  ROLE_KEYS.PLANT_HEAD,
  ROLE_KEYS.QA_MANAGER,
  ROLE_KEYS.QA_REVIEWER,
  ROLE_KEYS.QC,
  ROLE_KEYS.SUPERVISOR,
  ROLE_KEYS.OPERATOR,
];

// Map employee_role strings → canonical ROLE_KEYS.
// Layer 1: exact canonical key match (backend stores these exact strings).
// Layer 2: fuzzy match for human-readable / legacy variations.
const normalizeRoleString = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  // Strip spaces, underscores, hyphens and lowercase for uniform matching.
  const s = raw.toLowerCase().replace(/[\s_-]+/g, '');

  // ── Layer 1: exact canonical keys ────────────────────────────────────────
  if (s === 'admin')       return ROLE_KEYS.ADMIN;
  if (s === 'operator')    return ROLE_KEYS.OPERATOR;
  if (s === 'supervisor')  return ROLE_KEYS.SUPERVISOR;
  if (s === 'qareviewer')  return ROLE_KEYS.QA_REVIEWER;
  if (s === 'qamanager')   return ROLE_KEYS.QA_MANAGER;
  if (s === 'planthead')   return ROLE_KEYS.PLANT_HEAD;
  if (s === 'qc')          return ROLE_KEYS.QC;
  if (s === 'corporate')   return ROLE_KEYS.CORPORATE;
  if (s === 'planner')     return ROLE_KEYS.PLANNER;

  // ── Layer 2: fuzzy / human-readable variants ──────────────────────────────
  // Order matters: the specific production-management / planning / QC names must be
  // resolved BEFORE the generic "production" → OPERATOR rule at the bottom.
  if (s.includes('superadmin') || s.includes('externaladmin')) return ROLE_KEYS.ADMIN;
  if (s.includes('multiplant'))                                 return ROLE_KEYS.CORPORATE;
  if (s.includes('planthead') || s.includes('manufacturinghead') || s.includes('plantmanager')
    || s.includes('productionmanager') || s.includes('productionhead') || s.includes('manufacturingmanager')) return ROLE_KEYS.PLANT_HEAD;
  if (s.includes('planner'))                                    return ROLE_KEYS.PLANNER;
  if (s.includes('qcanalyst') || s.includes('qcmanager') || s.includes('qcreviewer')) return ROLE_KEYS.QC;
  if (s.includes('qamanager') || s.includes('qualitymanager') || s.includes('qualityhead') || s.includes('qahead')) return ROLE_KEYS.QA_MANAGER;
  if (s.includes('qareviewer') || s.includes('qareview'))      return ROLE_KEYS.QA_REVIEWER;
  if (s.startsWith('qa'))                                       return ROLE_KEYS.QA_REVIEWER;
  if (s.includes('qc') || s.includes('lims') || s.includes('lab')) return ROLE_KEYS.QC;
  if (s.includes('supervisor'))                                 return ROLE_KEYS.SUPERVISOR;
  if (s.includes('operator') || s.includes('production') || s.includes('manufacturing')) return ROLE_KEYS.OPERATOR;

  return null;
};

const readUserDetails = () => {
  try {
    const raw = localStorage.getItem('user_details');
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
};

export const useUserRole = () => {
  return useMemo(() => {
    const details = readUserDetails();
    const roleRows = [
      ...(Array.isArray(details?.roles) ? details.roles : []),
      ...(Array.isArray(details?.employee_role) ? details.employee_role : []),
    ];
    const rawRoles = roleRows
      .flatMap((r) =>
        typeof r === "string" ? [r] : [r?.employee_role, r?.role_name, r?.name]
      )
      .filter(Boolean);
    const normalized = rawRoles.map(normalizeRoleString).filter(Boolean);
    // Pick the highest-priority role this user has
    const primary =
      ROLE_PRIORITY.find((p) => normalized.includes(p)) || ROLE_KEYS.DEFAULT;
    return {
      primaryRole: primary,
      allRoles: normalized,
      isPlanner: normalized.includes(ROLE_KEYS.PLANNER),
      rawRoles,
      // Human-readable role name to display in the UI. When the user has multiple
      // roles, show the FIRST one (rawRoles[0]). Falls back to the normalized
      // primary role, then a generic label.
      displayRole: rawRoles[0] || primary || 'User',
      userId: details?.user_id || details?.id || null,
      username: details?.username || details?.user_name || details?.name || '',
    };
  }, []);
};

export default useUserRole;
